import {
    CheckCircleOutlined,
    ClockCircleOutlined,
    CloudUploadOutlined,
    DeleteOutlined,
    EditOutlined,
    FileExcelOutlined,
    FileImageOutlined,
    FilePdfOutlined,
    FileWordOutlined,
    FileZipOutlined,
    FolderOpenOutlined,
    LeftOutlined,
    PlusOutlined,
    StarFilled, StarOutlined,
    ThunderboltOutlined
} from '@ant-design/icons';
import {
    Avatar, Badge,
    Button,
    Card,
    Col,
    DatePicker,
    Divider,
    Input,
    InputNumber,
    Modal,
    Row,
    Select,
    Space,
    Statistic,
    Switch,
    Tag,
    Typography,
    Skeleton,
    Upload,
    message
} from 'antd';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import React, { useEffect, useMemo, useState } from 'react';
import { Project, Retailer, RetailerMailMessage } from '../../services/retailerApi';
import { Storage } from '../../services/storageApi';
import { Supplier } from '../../services/supplierApi';
import { User } from '../../services/userService';
import { deleteProjectAttachment, fetchProjectAttachmentBlob, uploadProjectAttachment } from '../../services/projectApi';

dayjs.extend(relativeTime);

const { Title, Text } = Typography;

interface ProjectDetailsProps {
  project: Project;
  retailer: Retailer;
  retailers?: Retailer[];
  suppliers?: Supplier[];
  storages?: Storage[];
  users?: User[];
  retailerEmails: RetailerMailMessage[];
  emailsLoading?: boolean;
  showBackButton?: boolean;
  useCrmChecklist?: boolean;
  onBack?: () => void;
  onAddTodo: (stage: 'todo' | 'in_progress', task: string) => void;
  onToggleTodo: (stage: 'todo' | 'in_progress', idx: number) => void;
  onDeleteTodo: (stage: 'todo' | 'in_progress', idx: number) => void;
  onThreadClick?: (threadId: string) => void;
  onToggleHotMemoryPin?: (id: string, type: string) => void;
  onRemoveHotMemory?: (id: string, type: string) => void;
  onClearHotMemory?: () => void;
  hotMemoryItems?: any[];
  getContactDisplayName: (retailer: Retailer, email?: string) => string;
  aiWarningsByAction: Record<string, string[]>;
  snapshotLoading?: boolean;
  getSnapshotPayload: (project: Project) => any;
  onUpdateProject?: (updates: Partial<Project>) => void;
  updateDraftCrmChecklist?: (stage: string, checklist: any[]) => void;
  handleUpdateRetailer?: (values: any, retailerId: string) => Promise<void>;
  toFormValues?: (retailer: Retailer) => any;
}

const ProjectDetails: React.FC<ProjectDetailsProps> = ({
  project,
  retailer,
  retailers = [],
  suppliers = [],
  storages = [],
  users = [],
  retailerEmails,
  emailsLoading = false,
  showBackButton,
  useCrmChecklist = false,
  onBack,
  onAddTodo,
  onToggleTodo,
  onDeleteTodo,
  onThreadClick,
  onToggleHotMemoryPin,
  onRemoveHotMemory,
  onClearHotMemory,
  hotMemoryItems = [],
  getContactDisplayName,
  snapshotLoading = false,
  getSnapshotPayload,
  onUpdateProject,
  updateDraftCrmChecklist,
  handleUpdateRetailer,
  toFormValues,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [customFields, setCustomFields] = useState(project.customFields || []);
  const [customFieldModalOpen, setCustomFieldModalOpen] = useState(false);
  const [customFieldDraft, setCustomFieldDraft] = useState({
    key: '',
    label: '',
    type: 'text',
    value: undefined as any,
    options: ''
  });
  const [editingCustomFieldIndex, setEditingCustomFieldIndex] = useState<number | null>(null);

  useEffect(() => {
    setCustomFields(project.customFields || []);
  }, [project._id, project.customFields]);

  const resetCustomFieldDraft = () => {
    setCustomFieldDraft({ key: '', label: '', type: 'text', value: undefined, options: '' });
    setEditingCustomFieldIndex(null);
  };

  const handleOpenAttachment = async (att: any) => {
    if (!project._id || !att?.itemId) {
      if (att?.downloadUrl) window.open(att.downloadUrl, '_blank');
      return;
    }
    try {
      const blob = await fetchProjectAttachmentBlob(project._id, att.itemId);
      const blobUrl = window.URL.createObjectURL(blob);
      window.open(blobUrl, '_blank');
      window.setTimeout(() => window.URL.revokeObjectURL(blobUrl), 60_000);
    } catch (error) {
      message.error("Impossible d'ouvrir le document");
    }
  };

  const openAddCustomField = () => {
    resetCustomFieldDraft();
    setCustomFieldModalOpen(true);
  };

  const openEditCustomField = (field: any, index: number) => {
    setCustomFieldDraft({
      key: field.key || '',
      label: field.label || '',
      type: field.type || 'text',
      value: field.value,
      options: (field.options || []).join(', '),
    });
    setEditingCustomFieldIndex(index);
    setCustomFieldModalOpen(true);
  };

  const snapshotPayload = getSnapshotPayload(project);
  const snapshotStatus = snapshotPayload?.snapshot?.push_pull_status || project.pushPullStatus || 'inconnu';
  const retailerId = retailer.id || retailer._id || '';
  
  const stageKey = useCrmChecklist
    ? (retailer.crm?.onboardingStage || "lead")
    : project.pushPullStatus === 'pull'
    ? 'in_progress'
    : 'todo';

  const checklistSource = useCrmChecklist ? retailer.crm?.checklists : undefined;
  const checklist = (checklistSource as any)?.[stageKey] || [];
  const taskBoardTasks = !useCrmChecklist ? (project.taskBoard as any)?.[stageKey] || [] : [];
  
  const completedCount = useCrmChecklist
    ? checklist.filter((task: any) => task.completed).length
    : taskBoardTasks.filter((task: any) => task.status === 'archived').length;
  const totalCount = useCrmChecklist ? checklist.length : taskBoardTasks.length;

  const safeRetailerEmails = Array.isArray(retailerEmails) ? retailerEmails : [];
  const attachedEmailIds = project.attachedEmailIds || [];
  const attachedConversationIds = project.attachedConversationIds || [];
  const projectEmails = attachedEmailIds.length
    ? safeRetailerEmails.filter((email) => attachedEmailIds.includes(email.id || ''))
    : attachedConversationIds.length
    ? safeRetailerEmails.filter((email) => attachedConversationIds.includes(email.conversationId || ''))
    : safeRetailerEmails;
  const persistedInterlocutors = (project.interlocutors || [])
    .map((interlocutor) =>
      interlocutor?.name ||
      [interlocutor?.firstName, interlocutor?.lastName].filter(Boolean).join(' ') ||
      interlocutor?.email
    )
    .filter((value): value is string => typeof value === 'string' && value.length > 0);
  const emailInterlocutors = Array.from(
    new Set(projectEmails.map((email) => getContactDisplayName(retailer, email.from)))
  ).filter((name): name is string => typeof name === 'string' && name.length > 0);
  const projectInterlocutorNames = emailInterlocutors.length
    ? emailInterlocutors
    : persistedInterlocutors;
  const hasPersistedInterlocutors = persistedInterlocutors.length > 0;
  const fallbackInterlocutor =
    persistedInterlocutors[0] ||
    (project.lastEmailFrom ? getContactDisplayName(retailer, project.lastEmailFrom) : undefined);

  const groupedProjectEmails = projectEmails.reduce(
    (acc, email) => {
      if (email.conversationId) {
        if (!acc.groups[email.conversationId]) acc.groups[email.conversationId] = [];
        acc.groups[email.conversationId].push(email);
      } else {
        acc.noThread.push(email);
      }
      return acc;
    },
    { groups: {} as Record<string, RetailerMailMessage[]>, noThread: [] as RetailerMailMessage[] }
  );

  const handleDetachThread = (threadId: string, messageIds: string[]) => {
    if (!onUpdateProject) return;
    Modal.confirm({
      title: 'Détacher cette conversation du projet ? ',
      content: 'Cette conversation ne sera plus prise en compte dans le projet.',
      okText: 'Détacher',
      okType: 'danger',
      cancelText: 'Annuler',
      onOk: () => {
        const nextAttachedConversationIds = attachedConversationIds.filter((id) => id !== threadId);
        const nextAttachedEmailIds = attachedEmailIds.filter((id) => !messageIds.includes(id));
        onUpdateProject({
          attachedConversationIds: nextAttachedConversationIds,
          attachedEmailIds: nextAttachedEmailIds,
        });
      },
    });
  };

  const projectThreadCards = [
    ...Object.entries(groupedProjectEmails.groups).map(([threadId, messages]) => {
      const sorted = [...messages].sort(
        (a, b) => new Date(b.receivedDateTime || '').getTime() - new Date(a.receivedDateTime || '').getTime()
      );
      const latest = sorted[0];
      const messageIds = messages.map((msg) => msg.id).filter(Boolean) as string[];
      const attachments = messages.flatMap((msg) =>
        (msg.attachments || []).filter((item: any) => !item.isInline).map((item: any) => ({
          ...item,
          key: `${msg.id || 'mail'}:${item.id}`,
        }))
      );
      const uniqueAttachments = Array.from(
        new Map(attachments.map((item: any) => [item.downloadUrl || item.key || item.id, item])).values()
      );
      const unreadCount = messages.filter((msg) => !msg.isRead).length;
      const summary =
        messages
          .map((msg) => msg.threadSummary)
          .find((value) => typeof value === "string" && value.trim().length > 0) ||
        messages
          .map((msg) => msg.aiSummary || msg.summary)
          .find((value) => typeof value === "string" && value.trim().length > 0);
      return {
        id: threadId,
        latest,
        count: messages.length,
        unread: unreadCount > 0,
        unreadCount,
        summary,
        attachments: uniqueAttachments,
        messageIds,
        participants: Array.from(new Set(messages.map(m => m.from).filter(Boolean))) as string[],
      };
    }),
    ...groupedProjectEmails.noThread.map((email) => ({
      id: email.id,
      latest: email,
      count: 1,
      unread: !email.isRead,
      unreadCount: email.isRead ? 0 : 1,
      summary: email.aiSummary || email.summary,
      messageIds: email.id ? [email.id] : [],
      attachments: (email.attachments || []).filter((item: any) => !item.isInline),
      participants: email.from ? [email.from] : [] as string[],
    })),
  ].sort(
    (a, b) => new Date(b.latest.receivedDateTime || '').getTime() - new Date(a.latest.receivedDateTime || '').getTime()
  );

  const [attachmentSort, setAttachmentSort] = useState<{ field: 'date' | 'name'; order: 'asc' | 'desc' }>({
    field: 'date',
    order: 'desc',
  });
  const [uploadingAttachment, setUploadingAttachment] = useState(false);
  const [deletingAttachmentId, setDeletingAttachmentId] = useState<string | null>(null);

  const apiBaseUrl = import.meta.env.VITE_API_URL || 'http://localhost:5050/api';
  const allAttachments = (project.attachments || []).map((att: any) => ({
    ...att,
    date: att.receivedDateTime,
    downloadUrl: att.itemId ? `${apiBaseUrl}/projects/${project._id}/attachments/${att.itemId}` : att.downloadUrl,
  }));
  const sortedAttachments = [...allAttachments].sort((a, b) => {
    let comparison = 0;
    if (attachmentSort.field === 'name') {
      comparison = (a.name || '').localeCompare(b.name || '');
    } else {
      comparison = new Date(a.date || 0).getTime() - new Date(b.date || 0).getTime();
    }
    return attachmentSort.order === 'asc' ? comparison : -comparison;
  });

  const getAttachmentIcon = (att: any) => {
    const name = String(att?.name || '').toLowerCase();
    const type = String(att?.contentType || '').toLowerCase();
    if (type.includes('pdf') || name.endsWith('.pdf')) return <FilePdfOutlined style={{ color: '#ef4444', fontSize: 24 }} />;
    if (
      type.includes('excel') ||
      type.includes('spreadsheet') ||
      ['.xls', '.xlsx', '.csv'].some((ext) => name.endsWith(ext))
    ) {
      return <FileExcelOutlined style={{ color: '#10b981', fontSize: 24 }} />;
    }
    if (
      type.includes('word') ||
      type.includes('document') ||
      ['.doc', '.docx', '.rtf'].some((ext) => name.endsWith(ext))
    ) {
      return <FileWordOutlined style={{ color: '#2563eb', fontSize: 24 }} />;
    }
    if (type.startsWith('image/') || ['.png', '.jpg', '.jpeg', '.webp', '.gif', '.bmp'].some((ext) => name.endsWith(ext))) {
      return <FileImageOutlined style={{ color: '#f59e0b', fontSize: 24 }} />;
    }
    if (['.zip', '.rar', '.7z', '.tar', '.gz'].some((ext) => name.endsWith(ext))) {
      return <FileZipOutlined style={{ color: '#64748b', fontSize: 24 }} />;
    }
    return <FolderOpenOutlined style={{ color: '#6366f1', fontSize: 24 }} />;
  };

  const handleUploadAttachment = async (file: File) => {
    if (!project._id) return false;
    setUploadingAttachment(true);
    try {
      const buffer = await file.arrayBuffer();
      const base64 = btoa(String.fromCharCode(...new Uint8Array(buffer)));
      const updated = await uploadProjectAttachment(project._id, {
        fileName: file.name,
        contentType: file.type,
        data: base64,
      });
      onUpdateProject?.({ attachments: updated.attachments || [] });
      message.success('Document importé');
    } catch (error) {
      message.error("Erreur lors de l'upload du document");
    } finally {
      setUploadingAttachment(false);
    }
    return false;
  };

  const handleDeleteAttachment = async (attachment: any) => {
    if (!project._id || !attachment?.itemId) return;
    setDeletingAttachmentId(attachment.itemId);
    try {
      const updated = await deleteProjectAttachment(project._id, attachment.itemId);
      onUpdateProject?.({ attachments: updated.attachments || [] });
      message.success('Document supprimé');
    } catch (error) {
      message.error('Erreur lors de la suppression');
    } finally {
      setDeletingAttachmentId(null);
    }
  };

  const progress = (() => {
    if (useCrmChecklist) {
      if (!totalCount) return 0;
      return Math.round((completedCount / totalCount) * 100);
    }
    const board = project.taskBoard || {};
    const allTasks = [...(board.todo || []), ...(board.in_progress || [])];
    if (!allTasks.length) return 0;
    const done = allTasks.filter((task: any) => task.status === 'archived').length;
    return Math.round((done / allTasks.length) * 100);
  })();

  const userOptions = useMemo(
    () => users.map((user) => ({
      value: user._id || (user as any).id,
      label: user.companyName || `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email || 'Utilisateur'
    })),
    [users]
  );

  const persistCustomFields = (nextFields: typeof customFields) => {
    setCustomFields(nextFields);
    onUpdateProject?.({ customFields: nextFields });
  };

  const handleRemoveCustomField = (idx: number) => {
    const next = customFields.filter((_, index) => index !== idx);
    persistCustomFields(next);
  };

  const handleSaveCustomField = () => {
    const label = customFieldDraft.label.trim();
    if (!label) return;
    const key = (customFieldDraft.key || label)
      .toLowerCase()
      .replace(/\s+/g, '_')
      .replace(/[^a-z0-9_]/g, '');
    const options = customFieldDraft.options
      .split(',')
      .map((opt) => opt.trim())
      .filter(Boolean);
    const payload = {
      key,
      label,
      type: customFieldDraft.type as any,
      value: customFieldDraft.value,
      options,
    };
    const next = [...customFields];
    if (editingCustomFieldIndex !== null) {
      next[editingCustomFieldIndex] = payload;
    } else {
      next.push(payload);
    }
    persistCustomFields(next);
    setCustomFieldModalOpen(false);
    resetCustomFieldDraft();
  };

  const formatCustomFieldValue = (field: any) => {
    if (field.type === 'boolean') return field.value ? 'Oui' : 'Non';
    if (field.type === 'date' && field.value) return dayjs(field.value).format('DD MMM YYYY');
    if (field.type === 'datetime' && field.value) return dayjs(field.value).format('DD MMM YYYY HH:mm');
    if (field.type === 'date_range' && Array.isArray(field.value)) {
      const [start, end] = field.value;
      if (start && end) return `${dayjs(start).format('DD MMM YYYY')} → ${dayjs(end).format('DD MMM YYYY')}`;
    }
    if (field.type === 'multi_select' && Array.isArray(field.value)) return field.value.join(', ');
    if (field.type === 'user') {
      const match = userOptions.find((opt) => opt.value === field.value);
      return match?.label || '—';
    }
    return field.value ?? '—';
  };

  const handleTaskToggle = (idx: number) => {
    if (useCrmChecklist) {
      const nextChecklist = checklist.map((task: any, taskIndex: number) =>
        taskIndex === idx ? { ...task, completed: !task.completed } : task
      );
      if (!retailerId) {
        updateDraftCrmChecklist?.(stageKey, nextChecklist);
      } else {
        const updatedRetailer: Retailer = {
          ...retailer,
          crm: {
            ...(retailer.crm || {}),
            onboardingStage: stageKey as any,
            checklists: {
              ...(retailer.crm?.checklists || {}),
              [stageKey]: nextChecklist,
            },
          },
        };
        const formValues = toFormValues?.(updatedRetailer);
        if (formValues) void handleUpdateRetailer?.(formValues, retailerId);
      }
    } else {
      onToggleTodo(stageKey as any, idx);
    }
  };

  const handleTaskDelete = (idx: number) => {
    if (useCrmChecklist) {
      const nextChecklist = checklist.filter((_: any, taskIndex: number) => taskIndex !== idx);
      if (!retailerId) {
        updateDraftCrmChecklist?.(stageKey, nextChecklist);
      } else {
        const updatedRetailer: Retailer = {
          ...retailer,
          crm: {
            ...(retailer.crm || {}),
            onboardingStage: stageKey as any,
            checklists: {
              ...(retailer.crm?.checklists || {}),
              [stageKey]: nextChecklist,
            },
          },
        };
        const formValues = toFormValues?.(updatedRetailer);
        if (formValues) void handleUpdateRetailer?.(formValues, retailerId);
      }
    } else {
      onDeleteTodo(stageKey as any, idx);
    }
  };

  return (
    <div className="project-detail-view-premium">
      <div className="detail-header-premium" style={{ marginBottom: 24, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          {showBackButton && (
            <Button
              icon={<LeftOutlined />}
              onClick={onBack}
              style={{ border: 'none', background: '#f8fafc', width: 40, height: 40, borderRadius: '12px' }}
            />
          )}
          <div>
            <Title level={4} style={{ margin: 0, fontWeight: 800 }}>{project.title}</Title>
            <div className="chronology-anchor" style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#64748b' }}>
              <ClockCircleOutlined />
              Dernière activité : {emailsLoading ? 'Chargement…' : project.lastEmailAt ? dayjs(project.lastEmailAt).format('DD MMM [à] HH:mm') : 'Aucune'}
            </div>
          </div>
        </div>
        <Space>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginRight: 16 }}>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontWeight: 800, color: '#0f172a' }}>{Math.round(progress)}%</div>
            </div>
            <div style={{ width: 60, height: 6, background: '#f1f5f9', borderRadius: 3, overflow: 'hidden' }}>
              <div style={{ width: `${progress}%`, height: '100%', background: '#3b82f6', borderRadius: 3 }} />
            </div>
          </div>
          <Button
            icon={<EditOutlined />}
            onClick={() => setIsEditing((prev) => !prev)}
          >
            {isEditing ? 'Terminer' : 'Modifier'}
          </Button>
        </Space>
      </div>

      <div className="detail-top-info" style={{ display: 'flex', gap: 24, alignItems: 'flex-start' }}>
        <div style={{ flex: 1 }}>
          <div style={{ marginBottom: 24 }}>
            <Text type="secondary" style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 700 }}>Résumé & Analyse IA</Text>
            <div style={{ marginTop: 8 }}>
              {snapshotLoading ? (
                <Skeleton active paragraph={{ rows: 2 }} title={false} />
              ) : (
                <Text style={{ fontSize: 15, color: '#334155', display: 'block' }}>
                  {snapshotPayload?.snapshot?.summary || snapshotPayload?.snapshot?.stateOfPlay || project.description || "Aucune description fournie pour ce projet opérationnel."}
                </Text>
              )}
            </div>
            <div style={{ marginTop: 16 }}>
              <Text type="secondary" style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 700 }}>Interlocuteurs</Text>
              <div style={{ marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {emailsLoading && !hasPersistedInterlocutors ? (
                  <Skeleton.Input active size="small" style={{ width: 180 }} />
                ) : projectInterlocutorNames.length > 0 ? (
                  projectInterlocutorNames.map((name) => (
                    <Tag key={name} color="geekblue" style={{ margin: 0, borderRadius: 10, fontWeight: 600 }}>
                      {name}
                    </Tag>
                  ))
                ) : fallbackInterlocutor ? (
                  <Tag color="geekblue" style={{ margin: 0, borderRadius: 10, fontWeight: 600 }}>
                    {fallbackInterlocutor}
                  </Tag>
                ) : (
                  <Text type="secondary" style={{ fontSize: 12 }}>Aucun interlocuteur</Text>
                )}
              </div>
            </div>
            <div style={{ marginTop: 16, padding: '12px 16px', borderRadius: 12, border: '1px solid #e2e8f0', background: '#f8fafc' }}>
              <Row gutter={[16, 16]}>
                <Col span={6}>
                  <Statistic
                    title="Statut IA"
                    value={snapshotLoading ? 'Analyse…' : String(snapshotStatus).toUpperCase()}
                    valueStyle={{ fontSize: 13, fontWeight: 700, color: snapshotStatus === 'pull' ? '#3b82f6' : '#64748b' }}
                  />
                </Col>
                <Col span={6}>
                  <Statistic
                    title="Priorité"
                    value={project.priority?.toUpperCase()}
                    valueStyle={{ fontSize: 13, fontWeight: 700, color: project.priority === 'high' ? '#ef4444' : '#10b981' }}
                  />
                </Col>
                <Col span={6}>
                  <Statistic
                    title="Messages"
                    value={emailsLoading ? '…' : projectEmails.length}
                    valueStyle={{ fontSize: 13, fontWeight: 700 }}
                  />
                </Col>
                <Col span={6}>
                  <Statistic
                    title="Complétion"
                    value={`${Math.round(progress)}%`}
                    valueStyle={{ fontSize: 13, fontWeight: 700 }}
                  />
                </Col>
              </Row>
            </div>

          </div>


          <Card size="small" title="Hot Memory" styles={{ header: { border: 'none', fontWeight: 700 } }} style={{ borderRadius: 12, border: '1px solid #e2e8f0', marginBottom: 24 }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {hotMemoryItems.length === 0 ? (
                <Text type="secondary" style={{ fontSize: 12 }}>Aucun contexte sélectionné</Text>
              ) : (
                hotMemoryItems.map((item: any) => (
                  <div key={`${item.type}:${item.id}`} style={{ display: 'flex', gap: 8, alignItems: 'center', justifyContent: 'space-between' }}>
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                      <Text style={{ fontSize: 12, fontWeight: 600 }}>{item.label || item.id}</Text>
                      <Text type="secondary" style={{ fontSize: 11 }}>{item.type}</Text>
                    </div>
                    <Space size={4}>
                      <Button
                        size="small"
                        icon={item.pinned ? <StarFilled /> : <StarOutlined />}
                        onClick={() => onToggleHotMemoryPin?.(item.id, item.type)}
                      />
                      <Button
                        size="small"
                        icon={<DeleteOutlined />}
                        onClick={() => onRemoveHotMemory?.(item.id, item.type)}
                      />
                    </Space>
                  </div>
                ))
              )}
            </div>
            <div style={{ marginTop: 12 }}>
              <Button size="small" onClick={onClearHotMemory}>Vider</Button>
            </div>
          </Card>
        </div>

        <div style={{ width: 320 }}>
          <Card
            size="small"
            title="Informations"
            style={{ borderRadius: 16, border: '1px solid #e2e8f0', marginBottom: 16 }}
            styles={{ header: { border: 'none', fontWeight: 700 } }}
            extra={
              isEditing ? (
                <Button size="small" type="primary" onClick={openAddCustomField}>
                  Ajouter champ
                </Button>
              ) : null
            }
          >
            <Space direction="vertical" size={12} style={{ width: '100%' }}>
              <div>
                <Text type="secondary" style={{ fontSize: 11 }}>Collaborateurs</Text>
                {isEditing ? (
                  <Select
                    mode="multiple"
                    allowClear
                    style={{ width: '100%', marginTop: 6 }}
                    placeholder="Ajouter des collaborateurs"
                    value={project.collaborators || []}
                    options={userOptions}
                    onChange={(vals) => onUpdateProject?.({ collaborators: vals })}
                  />
                ) : (
                  <div style={{ marginTop: 6, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                    {(project.collaborators || []).length > 0 ? (
                      (project.collaborators || []).map((id) => {
                        const match = userOptions.find((opt) => opt.value === id);
                        return (
                          <Tag key={id} style={{ margin: 0 }}>
                            {match?.label || 'Utilisateur'}
                          </Tag>
                        );
                      })
                    ) : (
                      <Text type="secondary">—</Text>
                    )}
                  </div>
                )}
              </div>
              <div>
                <Text type="secondary" style={{ fontSize: 11 }}>Distributeur</Text>
                {isEditing ? (
                  <Select
                    allowClear
                    showSearch
                    optionFilterProp="children"
                    style={{ width: '100%', marginTop: 6 }}
                    value={project.retailerId}
                    onChange={(val) => onUpdateProject?.({ retailerId: val })}
                    placeholder="Sélectionner un distributeur"
                  >
                    {retailers.map((r) => (
                      <Select.Option key={r._id || r.id} value={r._id || r.id}>
                        {r.name}
                      </Select.Option>
                    ))}
                  </Select>
                ) : (
                  <Text style={{ display: 'block', marginTop: 6 }}>
                    {retailers.find((r) => (r._id || r.id) === project.retailerId)?.name || '—'}
                  </Text>
                )}
              </div>
              <div>
                <Text type="secondary" style={{ fontSize: 11 }}>Fournisseur</Text>
                {isEditing ? (
                  <Select
                    allowClear
                    showSearch
                    optionFilterProp="children"
                    style={{ width: '100%', marginTop: 6 }}
                    value={project.supplierId}
                    onChange={(val) => onUpdateProject?.({ supplierId: val })}
                    placeholder="Sélectionner un fournisseur"
                  >
                    {suppliers.map((s) => (
                      <Select.Option key={s._id || s.id} value={s._id || s.id}>
                        {s.name}
                      </Select.Option>
                    ))}
                  </Select>
                ) : (
                  <Text style={{ display: 'block', marginTop: 6 }}>
                    {suppliers.find((s) => (s._id || s.id) === project.supplierId)?.name || '—'}
                  </Text>
                )}
              </div>
              <div>
                <Text type="secondary" style={{ fontSize: 11 }}>Entrepôt / Stockage</Text>
                {isEditing ? (
                  <Select
                    allowClear
                    showSearch
                    optionFilterProp="children"
                    style={{ width: '100%', marginTop: 6 }}
                    value={project.storageId}
                    onChange={(val) => onUpdateProject?.({ storageId: val })}
                    placeholder="Sélectionner un entrepôt"
                  >
                    {storages.map((storage) => (
                      <Select.Option key={storage._id || storage.id} value={storage._id || storage.id}>
                        {storage.name}
                      </Select.Option>
                    ))}
                  </Select>
                ) : (
                  <Text style={{ display: 'block', marginTop: 6 }}>
                    {storages.find((storage) => (storage._id || storage.id) === project.storageId)?.name || '—'}
                  </Text>
                )}
              </div>
              <Divider style={{ margin: '4px 0' }} />
              <div>
                <Text type="secondary" style={{ fontSize: 11 }}>Champs personnalisés</Text>
                {customFields.length === 0 ? (
                  <Text type="secondary" style={{ display: 'block', marginTop: 6 }}>—</Text>
                ) : (
                  <Space direction="vertical" size={8} style={{ width: '100%', marginTop: 8 }}>
                    {customFields.map((field, idx) => (
                      <div key={`${field.key}-${idx}`} style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                        <div style={{ minWidth: 0 }}>
                          <Text strong style={{ fontSize: 12 }}>{field.label}</Text>
                          <Text type="secondary" style={{ fontSize: 11, display: 'block' }}>
                            {formatCustomFieldValue(field)}
                          </Text>
                        </div>
                        {isEditing && (
                          <Space size={4}>
                            <Button size="small" onClick={() => openEditCustomField(field, idx)}>Modifier</Button>
                            <Button size="small" danger onClick={() => handleRemoveCustomField(idx)}>Supprimer</Button>
                          </Space>
                        )}
                      </div>
                    ))}
                  </Space>
                )}
              </div>
              {isEditing && customFields.length > 0 && (
                <div>
                  <Text type="secondary" style={{ fontSize: 11 }}>Champs visibles sur la card</Text>
                  <Select
                    mode="multiple"
                    style={{ width: '100%', marginTop: 6 }}
                    value={project.customFieldsDisplay || []}
                    onChange={(vals) => onUpdateProject?.({ customFieldsDisplay: vals })}
                    options={customFields.map((field) => ({ label: field.label, value: field.key }))}
                    placeholder="Sélectionner les champs à afficher"
                  />
                </div>
              )}
            </Space>
          </Card>

          <div style={{ background: '#ffffff', borderRadius: 16, padding: 16, border: '1px solid #e2e8f0' }}>
            <Text type="secondary" style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 700 }}>Dernières Nouvelles</Text>
            <div style={{ marginTop: 12 }}>
              {projectThreadCards[0] ? (
                <>
                  <Text strong style={{ fontSize: 13, display: 'block', marginBottom: 4 }}>{projectThreadCards[0].latest.subject}</Text>
                  <Typography.Paragraph
                    type="secondary"
                    style={{ fontSize: 12, margin: 0, height: 40 }}
                    ellipsis={{ rows: 2 }}
                  >
                    {projectThreadCards[0].latest.preview}
                  </Typography.Paragraph>
                  <Text type="secondary" style={{ fontSize: 11, marginTop: 8, display: 'block' }}>{dayjs(projectThreadCards[0].latest.receivedDateTime).fromNow()}</Text>
                </>
              ) : (
                <Text type="secondary" italic>Aucune mise à jour</Text>
              )}
            </div>
          </div>
        </div>
      </div>

      <Modal
        open={customFieldModalOpen}
        title={editingCustomFieldIndex !== null ? 'Modifier un champ' : 'Nouveau champ'}
        onCancel={() => {
          setCustomFieldModalOpen(false);
          resetCustomFieldDraft();
        }}
        onOk={handleSaveCustomField}
        okText={editingCustomFieldIndex !== null ? 'Enregistrer' : 'Ajouter'}
      >
        <Space direction="vertical" size={12} style={{ width: '100%' }}>
          <Input
            placeholder="Libellé"
            value={customFieldDraft.label}
            onChange={(e) => setCustomFieldDraft((prev) => ({ ...prev, label: e.target.value }))}
          />
          <Input
            placeholder="Clé (optionnel)"
            value={customFieldDraft.key}
            onChange={(e) => setCustomFieldDraft((prev) => ({ ...prev, key: e.target.value }))}
          />
          <Select
            value={customFieldDraft.type}
            style={{ width: '100%' }}
            onChange={(value) => setCustomFieldDraft((prev) => ({ ...prev, type: value }))}
          >
            <Select.Option value="text">Texte</Select.Option>
            <Select.Option value="long_text">Texte long</Select.Option>
            <Select.Option value="number">Nombre</Select.Option>
            <Select.Option value="boolean">Oui/Non</Select.Option>
            <Select.Option value="date">Date</Select.Option>
            <Select.Option value="datetime">Date & heure</Select.Option>
            <Select.Option value="date_range">Période</Select.Option>
            <Select.Option value="select">Liste</Select.Option>
            <Select.Option value="multi_select">Multi-liste</Select.Option>
            <Select.Option value="user">Collaborateur</Select.Option>
            <Select.Option value="url">URL</Select.Option>
            <Select.Option value="email">Email</Select.Option>
            <Select.Option value="phone">Téléphone</Select.Option>
            <Select.Option value="currency">Montant</Select.Option>
            <Select.Option value="percentage">Pourcentage</Select.Option>
          </Select>
          {['select', 'multi_select'].includes(customFieldDraft.type) && (
            <Input
              placeholder="Options (a,b,c)"
              value={customFieldDraft.options}
              onChange={(e) => setCustomFieldDraft((prev) => ({ ...prev, options: e.target.value }))}
            />
          )}
          <div>
            {['text', 'long_text', 'url', 'email', 'phone', 'currency', 'percentage'].includes(customFieldDraft.type) && (
              <Input
                placeholder="Valeur"
                value={customFieldDraft.value}
                onChange={(e) => setCustomFieldDraft((prev) => ({ ...prev, value: e.target.value }))}
              />
            )}
            {customFieldDraft.type === 'number' && (
              <InputNumber
                style={{ width: '100%' }}
                value={typeof customFieldDraft.value === 'number' ? customFieldDraft.value : undefined}
                onChange={(value) => setCustomFieldDraft((prev) => ({ ...prev, value }))}
              />
            )}
            {customFieldDraft.type === 'boolean' && (
              <Switch
                checked={Boolean(customFieldDraft.value)}
                onChange={(checked) => setCustomFieldDraft((prev) => ({ ...prev, value: checked }))}
              />
            )}
            {customFieldDraft.type === 'date' && (
              <DatePicker
                style={{ width: '100%' }}
                value={customFieldDraft.value ? dayjs(customFieldDraft.value) : null}
                onChange={(value) => setCustomFieldDraft((prev) => ({ ...prev, value: value ? value.toISOString() : undefined }))}
              />
            )}
            {customFieldDraft.type === 'datetime' && (
              <DatePicker
                style={{ width: '100%' }}
                showTime
                value={customFieldDraft.value ? dayjs(customFieldDraft.value) : null}
                onChange={(value) => setCustomFieldDraft((prev) => ({ ...prev, value: value ? value.toISOString() : undefined }))}
              />
            )}
            {customFieldDraft.type === 'date_range' && (
              <DatePicker.RangePicker
                style={{ width: '100%' }}
                value={
                  Array.isArray(customFieldDraft.value) && customFieldDraft.value.length === 2
                    ? [dayjs(customFieldDraft.value[0]), dayjs(customFieldDraft.value[1])]
                    : null
                }
                onChange={(values) =>
                  setCustomFieldDraft((prev) => ({
                    ...prev,
                    value: values ? values.map((val) => val?.toISOString()) : undefined,
                  }))
                }
              />
            )}
            {['select', 'multi_select'].includes(customFieldDraft.type) && (
              <Select
                mode={customFieldDraft.type === 'multi_select' ? 'multiple' : undefined}
                style={{ width: '100%' }}
                value={customFieldDraft.value}
                onChange={(value) => setCustomFieldDraft((prev) => ({ ...prev, value }))}
                options={customFieldDraft.options
                  .split(',')
                  .map((opt) => opt.trim())
                  .filter(Boolean)
                  .map((opt) => ({ label: opt, value: opt }))}
              />
            )}
            {customFieldDraft.type === 'user' && (
              <Select
                style={{ width: '100%' }}
                options={userOptions}
                value={customFieldDraft.value}
                onChange={(value) => setCustomFieldDraft((prev) => ({ ...prev, value }))}
              />
            )}
          </div>
        </Space>
      </Modal>

      <Card
        className="glass-card"
        style={{ marginTop: 24, borderRadius: 16, border: '1px solid #e2e8f0', background: '#ffffff' }}
        title={
          <Space>
            <CheckCircleOutlined style={{ color: '#10b981' }} />
            <span style={{ fontWeight: 700 }}>Checklist</span>
          </Space>
        }
        extra={
          <Space>
            <Tag color="cyan" icon={<ThunderboltOutlined />}>IA : Snapshot auto</Tag>
            <Tag color="blue" style={{ borderRadius: 6, fontWeight: 700 }}>
              {completedCount} / {totalCount}
            </Tag>
          </Space>
        }
      >
        <Space direction="vertical" style={{ width: '100%' }} size={16}>
          <div style={{ display: 'flex', gap: 8 }}>
            <Input
              placeholder="Ajouter une tâche personnalisée..."
              onPressEnter={(e) => {
                const val = (e.target as HTMLInputElement).value;
                if (val.trim()) {
                  if (useCrmChecklist) {
                    const nextChecklist = [...checklist, { task: val, completed: false }];
                    if (!retailerId) {
                      updateDraftCrmChecklist?.(stageKey, nextChecklist);
                    } else {
                      const updatedRetailer: Retailer = {
                        ...retailer,
                        crm: {
                          ...(retailer.crm || {}),
                          onboardingStage: stageKey as any,
                          checklists: {
                            ...(retailer.crm?.checklists || {}),
                            [stageKey]: nextChecklist,
                          },
                        },
                      };
                      const formValues = toFormValues?.(updatedRetailer);
                      if (formValues) void handleUpdateRetailer?.(formValues, retailerId);
                    }
                  } else {
                    onAddTodo(stageKey as any, val);
                  }
                  (e.target as HTMLInputElement).value = '';
                }
              }}
            />
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={(e) => {
                const input = (e.currentTarget.closest('.ant-card-body')?.querySelector('input') as HTMLInputElement | null);
                const val = input?.value || '';
                if (val.trim()) {
                  if (useCrmChecklist) {
                    const nextChecklist = [...checklist, { task: val, completed: false }];
                    if (!retailerId) {
                      updateDraftCrmChecklist?.(stageKey, nextChecklist);
                    } else {
                      const updatedRetailer: Retailer = {
                        ...retailer,
                        crm: {
                          ...(retailer.crm || {}),
                          onboardingStage: stageKey as any,
                          checklists: {
                            ...(retailer.crm?.checklists || {}),
                            [stageKey]: nextChecklist,
                          },
                        },
                      };
                      const formValues = toFormValues?.(updatedRetailer);
                      if (formValues) void handleUpdateRetailer?.(formValues, retailerId);
                    }
                  } else {
                    onAddTodo(stageKey as any, val);
                  }
                  if (input) input.value = '';
                }
              }}
            />
          </div>
          <div style={{ maxHeight: 320, overflowY: 'auto' }}>
            {taskBoardTasks.length === 0 && checklist.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '12px' }}>
                <Text type="secondary" italic style={{ display: 'block', marginBottom: 8 }}>Aucune tâche définie pour ce sujet.</Text>
              </div>
            ) : (
              (useCrmChecklist ? checklist : taskBoardTasks).map((item: any, idx: number) => {
                const isCompleted = useCrmChecklist ? item.completed : item.status === 'archived';
                const taskTitle = useCrmChecklist ? item.task : item.title;
                return (
                  <div key={idx} className={`checklist-item project-checklist-item ${isCompleted ? 'is-completed' : ''}`} style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    padding: '10px 8px',
                    borderRadius: 8,
                    marginBottom: 4,
                    background: isCompleted ? '#f8fafc' : 'transparent',
                    border: '1px solid transparent',
                    transition: 'all 0.2s',
                    cursor: 'pointer'
                  }} onClick={() => handleTaskToggle(idx)}>
                    <div style={{
                      width: 18,
                      height: 18,
                      borderRadius: 6,
                      border: `1.5px solid ${isCompleted ? '#10b981' : '#cbd5e1'}`,
                      background: isCompleted ? '#10b981' : 'transparent',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      {isCompleted && <CheckCircleOutlined style={{ fontSize: 12, color: '#fff' }} />}
                    </div>
                    <Text className="project-checklist-text" style={{
                      fontSize: 14,
                      color: isCompleted ? '#94a3b8' : '#1e293b',
                      textDecoration: isCompleted ? 'line-through' : 'none',
                      flex: 1
                    }}>{taskTitle}</Text>
                    {!useCrmChecklist && item.source && (
                      <Tag color={item.source === 'ai' ? 'purple' : item.source === 'default' ? 'blue' : 'green'}>
                        {item.source === 'ai' ? 'IA' : item.source === 'default' ? 'Default' : 'Manuel'}
                      </Tag>
                    )}
                    <Button
                      type="text"
                      danger
                      size="small"
                      icon={<DeleteOutlined style={{ fontSize: 12 }} />}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleTaskDelete(idx);
                      }}
                      style={{ marginLeft: 'auto', color: '#94a3b8' }}
                    />
                  </div>
                );
              })
            )}
          </div>
        </Space>
      </Card>

      <div className="carousel-section" style={{ marginBottom: 32, marginTop: 32 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <Text strong style={{ fontSize: 16 }}>Timeline des Échanges</Text>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {emailsLoading ? 'Chargement…' : `${projectThreadCards.length} conversations`}
          </Text>
        </div>
        <div className="premium-carousel" style={{ display: 'flex', gap: 16, overflowX: 'auto', paddingBottom: 16 }}>
          {emailsLoading ? (
            Array.from({ length: 3 }).map((_, idx) => (
              <div
                key={`skeleton-${idx}`}
                style={{
                  minWidth: 300,
                  maxWidth: 300,
                  padding: 16,
                  background: '#fff',
                  borderRadius: 12,
                  border: '1px solid #e2e8f0',
                }}
              >
                <Skeleton active title={{ width: '70%' }} paragraph={{ rows: 3 }} />
              </div>
            ))
          ) : (
            projectThreadCards.map((thread) => (
              <div
                key={thread.id}
                className={`carousel-item-mail ${thread.unread ? 'unread' : ''}`}
                style={{
                  minWidth: 300,
                  maxWidth: 300,
                  padding: 16,
                  background: '#fff',
                  borderRadius: 12,
                  border: '1px solid #e2e8f0',
                  borderLeft: thread.unread ? '4px solid #3b82f6' : '1px solid #e2e8f0',
                  cursor: 'pointer'
                }}
                onClick={() => {
                  if (thread.id) {
                    onThreadClick?.(thread.id);
                  }
                }}
              >
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <Avatar size="small" style={{ backgroundColor: '#1e293b' }}>{thread.latest?.from?.charAt(0).toUpperCase() || '?'}</Avatar>
                  {thread.unreadCount > 0 && (
                    <Badge
                      count={thread.unreadCount}
                      size="small"
                      style={{ backgroundColor: '#ef4444' }}
                    />
                  )}
                </div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  {(attachedConversationIds.includes(thread.id || '') ||
                    (thread.messageIds || []).some((id: string) => attachedEmailIds.includes(id))) && (
                    <Button
                      type="text"
                      danger
                      size="small"
                      icon={<DeleteOutlined style={{ fontSize: 12 }} />}
                      onClick={(event) => {
                        event.stopPropagation();
                        if (thread.id) handleDetachThread(thread.id, thread.messageIds || []);
                      }}
                    />
                  )}
                  {thread.summary && (
                    <Tag color="purple" style={{ margin: 0, fontSize: 10, borderRadius: 4 }}>Résumé IA</Tag>
                  )}
                  <Text type="secondary" style={{ fontSize: 11 }}>{thread.latest?.receivedDateTime ? dayjs(thread.latest.receivedDateTime).format('DD MMM') : '?'}</Text>
                </div>
              </div>
              <Text strong style={{ fontSize: 13, display: 'block', marginBottom: 8 }} ellipsis>{thread.latest?.subject || '(Sans objet)'}</Text>
              
              <div style={{
                padding: '8px',
                background: '#f5f3ff',
                borderRadius: '8px',
                marginBottom: 12,
                border: '1px dashed #ddd6fe'
              }}>
                <Typography.Paragraph
                  type="secondary"
                  style={{ fontSize: 11, margin: 0, color: '#6d28d9' }}
                  ellipsis={{ rows: 2 }}
                >
                  {thread.summary || thread.latest?.preview}
                </Typography.Paragraph>
              </div>
              </div>
            ))
          )}
        </div>
      </div>

      <div className="carousel-section" style={{ marginBottom: 32 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <Text strong style={{ fontSize: 16 }}>Documents & Pièces Jointes</Text>
          <Select
            size="small"
            value={attachmentSort.field}
            onChange={(value) => setAttachmentSort((prev) => ({ ...prev, field: value as 'date' | 'name' }))}
            style={{ width: 140 }}
            options={[
              { value: 'date', label: 'Trier par date' },
              { value: 'name', label: 'Trier par nom' },
            ]}
          />
        </div>
        <div className="premium-carousel" style={{ display: 'flex', gap: 16, overflowX: 'auto', paddingBottom: 16 }}>
          <Upload
            showUploadList={false}
            beforeUpload={handleUploadAttachment}
            disabled={uploadingAttachment}
          >
            <div
              className="carousel-item-attachment"
              style={{
                cursor: uploadingAttachment ? 'wait' : 'pointer',
                width: 220,
                height: 110,
                borderRadius: 16,
                border: '1px dashed #cbd5f5',
                background: 'linear-gradient(135deg, #eef2ff 0%, #f8fafc 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 10,
                padding: 12,
              }}
            >
              <div style={{ width: 44, height: 44, borderRadius: 12, background: '#fff', border: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <CloudUploadOutlined style={{ fontSize: 22, color: '#6366f1' }} />
              </div>
              <div style={{ textAlign: 'left', maxWidth: 120 }}>
                <Text strong style={{ display: 'block', fontSize: 13 }} ellipsis>
                  Ajouter un document
                </Text>
                <Text type="secondary" style={{ fontSize: 11 }}>Upload SharePoint</Text>
              </div>
            </div>
          </Upload>

          {sortedAttachments.length === 0 ? (
            <div style={{ flex: 1, textAlign: 'center', padding: '24px', background: '#f8fafc', borderRadius: 16 }}>
              <Text type="secondary">Aucun document rattaché</Text>
            </div>
          ) : (
            sortedAttachments.map((att: any) => (
              <div
                key={att.itemId || att.downloadUrl}
                className="carousel-item-attachment"
                onClick={() => handleOpenAttachment(att)}
                style={{
                  cursor: 'pointer',
                  width: 220,
                  height: 110,
                  borderRadius: 16,
                  border: '1px solid #e2e8f0',
                  background: '#ffffff',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  padding: 12,
                  position: 'relative',
                }}
              >
                {att.source === 'manual' && (
                  <Button
                    type="text"
                    danger
                    size="small"
                    icon={<DeleteOutlined style={{ fontSize: 12 }} />}
                    onClick={(event) => {
                      event.stopPropagation();
                      handleDeleteAttachment(att);
                    }}
                    loading={deletingAttachmentId === att.itemId}
                    style={{ position: 'absolute', top: 6, right: 6 }}
                  />
                )}
                <div style={{ width: 52, height: 52, borderRadius: 14, background: '#f8fafc', border: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  {getAttachmentIcon(att)}
                </div>
                <div style={{ minWidth: 0, maxWidth: 120 }}>
                  <Text strong style={{ fontSize: 13, display: 'block' }} ellipsis>
                    {att.name || 'Fichier'}
                  </Text>
                  <Text type="secondary" style={{ fontSize: 11 }}>{att.size ? `${(att.size / 1024).toFixed(1)} KB` : '0 KB'}</Text>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default ProjectDetails;
